import { Grid2, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  ASSESSMENT_RESULT_MAP,
  ASSESSMENT_RISK_MAP,
} from '@constants/assessment';
import referenceRanges from '@constants/reference-range/index';

import { useAssessmentResult } from '@hooks/useAssessmentResult';

import { displayAssessmentReferenceRange } from '@utils/assessment';

import { Gender, ReferenceRangeRiskLevel } from 'generated/types';

import RangeList from '../Common/RangeList';
import RangeSection from '../Common/RangeSection';

const messages = defineMessages({
  title: {
    id: 'bhaResults.referenceRanges',
    defaultMessage: 'Reference Ranges',
  },
  below: {
    id: 'bhaResults.below',
    defaultMessage: 'Below',
  },
  above: {
    id: 'bhaResults.above',
    defaultMessage: 'Above',
  },
  applicable: {
    id: 'bhaResults.applicable',
    defaultMessage: 'Applicable to your age and biological sex.',
  },
  range: {
    id: 'bhaResults.range',
    defaultMessage: 'Range',
  },
});

interface ReferenceRangeProps {
  code: string;
}

export default function ReferenceRange({ code }: ReferenceRangeProps) {
  const { formatMessage } = useIntl();
  const { loading, profile, getResult } = useAssessmentResult();
  const metricData = ASSESSMENT_RESULT_MAP.get(code);

  const metric = getResult(code);
  const sexReferenceRange =
    profile?.sex && referenceRanges.get(code)?.get(profile.sex);
  const generalReferenceRange = referenceRanges.get(code)?.get(Gender.unknown);
  const referenceRange = sexReferenceRange ?? generalReferenceRange;

  if (!metricData || !metric || !referenceRange) {
    return null;
  }

  const {
    applicable = false,
    // includeUnits = false,
    subtext = [formatMessage(messages.range)],
  } = metricData;

  const refRanges = Array.from(referenceRange.entries()).map(
    ([risk, ranges]) => {
      const { color, condition, label } = ASSESSMENT_RISK_MAP[risk];
      let withinRange = false;

      /**
       * Transforms ranges to friendly text
       * RANGES: [{code: 'systolic', upper: 90}, {code: 'diastolic', upper: 60}]
       * REF: ['Below 90', 'or', 'Below 60']
       */
      const ref = ranges.reduce<string[]>((acc, curr, index) => {
        const { lower, upper } = curr;
        const {
          isInRange,
          text: { id, range },
        } = displayAssessmentReferenceRange({
          lowerLimit: lower,
          upperLimit: upper,
          metric,
          risk,
          code,
        });
        acc = acc.concat([
          `${id ? `${formatMessage({ id })}` : ''} ${range}`,
          ...(index !== ranges.length - 1 && condition
            ? [formatMessage({ id: condition })]
            : []),
        ]);

        withinRange = isInRange;
        return acc;
      }, []);

      return {
        isInRange: withinRange,
        range: ref,
        color,
        label,
      };
    }
  );

  return (
    <RangeSection
      loading={loading}
      /* Age/sex-dependent ref range disclaimer */
      subtitle={applicable ? formatMessage(messages.applicable) : undefined}
      /* Metric name */
      title={formatMessage(messages.title)}
    >
      {/* Displays the metric unit(s) or defaults to 'Range' 
        e.g. BP: ['Systolic', '', 'Diastolic']
        e.g. HR: undefined -> 'Range'
        */}
      <Grid2 container size={{ xs: 7 }}>
        {subtext.map((text, index) => (
          <Grid2
            key={`${code}-subtext-${index}`}
            sx={{
              ...(index === 0 && {
                pl: 2,
              }),
            }}
            size={{
              xs:
                subtext.length > 1
                  ? index === 0 || text.length - 1 === index
                    ? 5
                    : 2
                  : 7,
            }}
          >
            <Typography
              fontWeight="bold"
              key={`${code}-subtext-${index}`}
              my={1}
              variant="body2"
            >
              {text}
            </Typography>
          </Grid2>
        ))}
      </Grid2>

      {/* Map through the metric's reference ranges (as per AHI) */}
      {refRanges.map(({ color, isInRange, range, label }, index) => {
        let level = ReferenceRangeRiskLevel.ideal;
        switch (color) {
          case 'success.main':
            level = ReferenceRangeRiskLevel.normal;
            break;
          case 'warning.dark':
            level = ReferenceRangeRiskLevel.moderate;
            break;
          case 'error.main':
            level = ReferenceRangeRiskLevel.high;
            break;
        }
        return (
          <RangeList
            code={code}
            isInRange={isInRange}
            key={`${code}reference-range-${index}`}
            level={level}
            levelText={formatMessage({ id: label })}
            range={range}
          />
        );
      })}
    </RangeSection>
  );
}
